import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useRelativeTime } from "@/hooks/use-relative-time";
import { getInitials } from "@/lib/text-utils";
import type { RecipeBoxInvitation } from "@/lib/types";
import { formatInvitationStatus, normalizeInvitationStatus } from "../_lib/invitations";

export type InvitationListItemProps = Readonly<{
  invitation: RecipeBoxInvitation;
  canManage: boolean;
  onCancel: (invitation: RecipeBoxInvitation) => void;
  onResend: (invitation: RecipeBoxInvitation) => void;
  activeAction: "cancel" | "resend" | null;
}>;

export const InvitationListItem = ({
  invitation,
  canManage,
  onCancel,
  onResend,
  activeAction,
}: InvitationListItemProps) => {
  const displayName = invitation.recipientName ?? invitation.recipientEmail ?? invitation.invitedUserUid ?? "Pending member";
  const email = invitation.recipientEmail;
  const invited = useRelativeTime(invitation.createdAt);
  const status = normalizeInvitationStatus(invitation.status);
  const statusLabel = formatInvitationStatus(status);
  const isPending = status === "pending";
  const isExpired = status === "expired";
  const canResend = canManage && (isPending || isExpired);
  const canCancel = canManage && isPending;
  const isCancelling = activeAction === "cancel";
  const isResending = activeAction === "resend";

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-dashed border-muted-foreground/40 bg-muted/10 p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <Avatar className="h-10 w-10 border border-muted-foreground/40 bg-background">
          <AvatarFallback className="text-sm font-medium text-muted-foreground">{getInitials(displayName)}</AvatarFallback>
        </Avatar>
        <div className="space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium leading-none text-foreground">{displayName}</p>
            <Badge
              variant={isPending ? "outline" : "secondary"}
              className={`text-[10px] uppercase tracking-wide${isExpired ? " text-destructive border-destructive/50" : ""}`}
            >
              {statusLabel}
            </Badge>
          </div>
          {email && email !== displayName ? (
            <p className="text-xs text-muted-foreground">{email}</p>
          ) : null}
          {invited ? <p className="text-xs text-muted-foreground">Invited {invited}</p> : null}
        </div>
      </div>
      {(canResend || canCancel) ? (
        <div className="flex flex-col gap-1 sm:flex-row">
          {canResend ? (
            <Button variant="ghost" size="sm" onClick={() => onResend(invitation)} disabled={isResending || isCancelling}>
              {isResending ? "Resending..." : "Resend"}
            </Button>
          ) : null}
          {canCancel ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => onCancel(invitation)}
              disabled={isCancelling || isResending}
            >
              {isCancelling ? "Cancelling..." : "Cancel"}
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

